import {Injectable, signal, WritableSignal} from '@angular/core';
import {BehaviorSubject, Observable, of, tap} from 'rxjs';
import {catchError, finalize, first, map, switchMap} from 'rxjs/operators';
import {AuthUserModel} from '../models/user.model';
import {ITokenDecoded, TokenModel} from '../models/token.model';
import {environment} from 'src/environments/environment';
import {ActivatedRouteSnapshot, Router} from '@angular/router';
import {JwtHelperService} from '@auth0/angular-jwt';
import {AccessControl} from '../../../../_config/access-control';
import {ApolloHelperService} from "../../../shared/services/apollo-helper.service";
import {gql} from "apollo-angular";
import {AuthHTTPService} from "./auth-http/auth-http.service";

/**
 * Refleja los datos del usuario autenticado puede ser undefined como valor por defecto o un AuthUserModel
 *
 * @author Carlos Duardo <carlos.duardo@qualud.com>
 */
export type AuthUserType = AuthUserModel | undefined;

export const LOGIN_ROUTE = '/auth/login';

export const AUTH_USER_QUERY = gql`
  fragment userInfo on User {
    id
    username
    name
    lastname
    email
    gender
    mobile
    roles
    roles
    dni
    enabled
    locale
    birthdate
    lastLogin
    updatedTime
  }
  query getUser($id: ID!) {
    user(id: $id) {
      ...userInfo
      groups(order: {id: "ASC"}) {
        collection {
          id
          name
        }
      }
    }
  }
`

@Injectable({
  providedIn: 'root',
})
export class AuthService {

  private _jwtDecodedToken: any;

  // public fields
  public static readonly LS_TOKEN_KEY = `${environment.appVersion}-${environment.USERDATA_KEY}`;
  public static readonly LS_AUTH_USER_ID_KEY = `${environment.appVersion}-${environment.USERDATA_KEY}-user_id`;
  public static readonly LS_REFRESH_TOKEN_KEY = `${environment.appVersion}-refresh_token`;
  public static readonly LS_APP_METRONIC_LAYOUT_CONFIG_KEY = `${environment.appVersion}-layoutConfig`;

  currentUser: WritableSignal<AuthUserType> = signal(undefined);

  isLoadingSubject: BehaviorSubject<boolean>;

  get currentUserValue(): AuthUserType {
    return this.currentUser();
  }

  set currentUserValue(user: AuthUserType) {
    this.currentUser.set(user);
  }

  constructor(
    private authHttpService: AuthHTTPService,
    private router: Router,
    private apolloHelperService: ApolloHelperService
  ) {
    this.isLoadingSubject = new BehaviorSubject<boolean>(false);
    this.currentUser.set(undefined)
  }

  // public methods
  login(email: string, password: string): Observable<AuthUserType> {
    this.isLoadingSubject.next(true);
    return this.authHttpService.login(email, password).pipe(
      tap((auth: TokenModel) => AuthService.setAuthFromLocalStorage(auth)),
      switchMap(() => this.getUserByToken()),
      catchError((err) => {
        console.error('login-error', err);
        return of(undefined);
      }),
      finalize(() => this.isLoadingSubject.next(false))
    );
  }

  async logout(
    options?: {
      reloadPage?: boolean,
      redirectToCurrentUrl?: boolean
      reloadPageDelay?: number,
      context?: Record<string, any>;
    }) {

    AuthService.removeAuthFormLocalStorage();
    this.currentUserValue = undefined;

    const reloadPage: boolean = options?.reloadPage || false;
    const redirectToCurrentUrl: boolean = options?.redirectToCurrentUrl || true;
    const reloadPageDelay: number = options?.reloadPageDelay || 0;
    const context: Record<string, any> = options?.context || {};

    const returnUrl = window.location.pathname;
    if (redirectToCurrentUrl && !returnUrl?.includes('/auth') && !returnUrl?.includes('/error')) {
      context.redirectUrl = returnUrl;
    }

    const extras = Object.keys(context).length > 0
      ? {queryParams: context}
      : {}

    await this.router.navigate([LOGIN_ROUTE], extras);

    if (reloadPage) {
      if (reloadPageDelay > 0) {
        setTimeout(() => {
          document.location.reload();
        }, reloadPageDelay)

        return;
      }
      document.location.reload();
    }
  }

  getUserByToken(): Observable<AuthUserType> {
    const token = AuthService.getAuthFromLocalStorage();
    if (undefined === token) {
      return of(undefined);
    }

    this.isLoadingSubject.next(true);
    //TODO: revisar si cambiar por el metodo fetchUserByIRI
    return this.authHttpService.getUserByTokenStorage().pipe(
      first(),
      map(user => {
        // @ts-ignore
        user.id = user['@id'];
        if (user.center) {
          // @ts-ignore
          user.center = {...user.center, id: user.center['@id']};
        }
        user.groups = user.groups.map((group: any) => group['@id']);
        return user
      }),
      tap((user: AuthUserType) => {

        if (!user) {
          this.logout().then();
          return;
        }

        this.jwtDecodedToken = this.decodeJWTToken();
        this.currentUserValue = user;
      }),
      finalize(() => this.isLoadingSubject.next(false))
    );
  }

  // need create new user then login
  registration(user: AuthUserModel): Observable<AuthUserType> {
    this.isLoadingSubject.next(true);
    return this.authHttpService.registerUser(user).pipe(
      switchMap(() => this.login(user.email, user.password)),
      catchError((err) => {
        console.error('registration-error', err);
        return of(undefined);
      }),
      finalize(() => this.isLoadingSubject.next(false))
    );
  }

  forgotPassword(email: string): Observable<any> {
    this.isLoadingSubject.next(true);
    return this.authHttpService
      .forgotPassword(email)
      .pipe(finalize(() => this.isLoadingSubject.next(false)));
  }

  refreshToken() {

    const userRefreshToken = localStorage.getItem(AuthService.LS_REFRESH_TOKEN_KEY);
    if (null === userRefreshToken) {
      return of(undefined);
    }

    return this.authHttpService.refreshToken(userRefreshToken).pipe(
      first(),
      tap((newToken) => {
        AuthService.setAuthFromLocalStorage(newToken)
      })
    );
  }

  static getAuthFromLocalStorage(): string | undefined {
    try {
      const lsValue = localStorage.getItem(AuthService.LS_TOKEN_KEY);
      if (!lsValue) {
        return undefined;
      }

      return lsValue;
    } catch (error) {
      console.error(error);
      return undefined;
    }
  }

  static removeAuthFormLocalStorage() {
    localStorage.removeItem(AuthService.LS_TOKEN_KEY);
    localStorage.removeItem(AuthService.LS_AUTH_USER_ID_KEY);
    localStorage.removeItem(AuthService.LS_REFRESH_TOKEN_KEY);
    localStorage.removeItem(AuthService.LS_APP_METRONIC_LAYOUT_CONFIG_KEY);
  }

  isAuthorized(roles: string | string[]) {
    roles = Array.isArray(roles) ? roles : [roles];
    return undefined !== this.currentUserValue && this.currentUserValue.roles.some(role => roles.indexOf(role.toString()) >= 0);
  }

  userHasAccessToRoute(route: ActivatedRouteSnapshot | string | null = null): Promise<boolean> {
    return new Promise(async (resolve) => {

      if (!AccessControl.CHECK_ACCESS) {
        resolve(true);
        return;
      }

      //get the roles of the authenticated user
      const userRoles = this.currentUserValue?.roles;

      //user has no roles
      if (undefined === userRoles || 0 === userRoles.length) {

        if (AccessControl.GRANT_ACCESS_IF_USER_HAS_NO_ROLE) {
          resolve(true);
        } else {
          resolve(false);
        }

        return;
      }

      //get all allowed route by value
      const accessControl: any = AccessControl.getAccessControlListByRole()
      let permits: any[] = [];

      //we get all permissions for each user value
      userRoles.forEach(userRole => {
        if (accessControl.hasOwnProperty(userRole)) {
          permits.push(...accessControl[userRole]);
        }
      });

      if (!permits.includes(route)) {

        resolve(false);

        // In the case of bad configuration that by mistake we define that the route to redirect is a
        // route under access check and our user does not have access to said route will be logged out of the platform
        // and redirected to login.
        // And if the initial page of our platform after login is the same as redirection route then our user will never be able to access the platform
        if (AccessControl.REDIRECT_ON_ACCESS_DENIED_ROUTE.path === route) {
          this.logout();
          return
        }

        await this.router.navigateByUrl(AccessControl.REDIRECT_ON_ACCESS_DENIED_ROUTE.url)
        return;
      }


      resolve(true);

    });
  }

  /**
   * Decode the JWT token used to authenticate, so we can get the metadata sent in the token
   */
  get jwtDecodedToken(): ITokenDecoded | undefined {
    return this._jwtDecodedToken;
  }

  set jwtDecodedToken(value: ITokenDecoded | undefined) {
    this._jwtDecodedToken = value;
  }

  private decodeJWTToken(): undefined | ITokenDecoded {
    const token = AuthService.getAuthFromLocalStorage();
    if (!token) {
      return undefined;
    }
    return <ITokenDecoded>(new JwtHelperService()).decodeToken(token);
  }

  fetchUserByIRI(iri: string, freezeResult?: boolean) {
    return this.apolloHelperService.fetchById({
      query: AUTH_USER_QUERY,
      id: iri,
      freezeResult,
      convertTo: 'promise'
    });
  }

  private static setAuthFromLocalStorage(auth: TokenModel): boolean {
    if (auth && auth.token) {
      const helper = new JwtHelperService();
      const decodedToken = helper.decodeToken(auth.token);
      localStorage.setItem(AuthService.LS_TOKEN_KEY, auth.token);
      localStorage.setItem(AuthService.LS_AUTH_USER_ID_KEY, decodedToken.user.id);
      localStorage.setItem(AuthService.LS_REFRESH_TOKEN_KEY, auth.refresh_token);
      return true;
    }
    return false;
  }

}
